import type { ReactNode } from 'react'
import { initials } from '@/lib/format'
import type { PropertyHealth, Stance } from '@/types/entities'

/* ---------- Eyebrow ---------- */
export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="eyebrow">{children}</div>
}

/* ---------- Page header (eyebrow + mixed-weight title + actions) ---------- */
export function PageHeader({
  eyebrow, title, sub, actions, breadcrumb,
}: {
  eyebrow?: ReactNode
  title: ReactNode
  sub?: ReactNode
  actions?: ReactNode
  breadcrumb?: ReactNode
}) {
  return (
    <div className="page-header">
      {breadcrumb && <div className="breadcrumb">{breadcrumb}</div>}
      <div className="page-header-row">
        <div className="page-header-titles">
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <h1 className="mixed page-title">{title}</h1>
          {sub && <div className="page-sub muted">{sub}</div>}
        </div>
        {actions && <div className="page-header-actions">{actions}</div>}
      </div>
    </div>
  )
}

/* ---------- Section card ---------- */
export function SectionCard({
  title, eyebrow, right, children, pad = true, className = '',
}: {
  title?: ReactNode; eyebrow?: ReactNode; right?: ReactNode
  children: ReactNode; pad?: boolean; className?: string
}) {
  return (
    <section className={`card ${className}`}>
      {(title || right || eyebrow) && (
        <header className="card-head">
          <div>
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            {title && <h2 className="card-title mixed">{title}</h2>}
          </div>
          {right && <div className="card-head-right">{right}</div>}
        </header>
      )}
      <div className={pad ? 'card-body' : 'card-body card-body--flush'}>{children}</div>
    </section>
  )
}

/* ---------- Generic card ---------- */
export function Card({ children, className = '', onClick }: { children: ReactNode; className?: string; onClick?: () => void }) {
  return <div className={`card ${onClick ? 'card--clickable' : ''} ${className}`} onClick={onClick}>{children}</div>
}

/* ---------- Badge ---------- */
export type BadgeTone = 'ok' | 'warn' | 'alert' | 'neutral' | 'info' | 'navy'
export function Badge({
  children, tone = 'neutral', variant = 'soft', dot = false,
}: { children: ReactNode; tone?: BadgeTone; variant?: 'soft' | 'solid' | 'outline'; dot?: boolean }) {
  return (
    <span className={`badge badge--${tone} badge--${variant}`}>
      {dot && <span className="badge-dot" />}
      {children}
    </span>
  )
}

const HEALTH_TONE: Record<PropertyHealth, BadgeTone> = { Healthy: 'ok', Watch: 'warn', 'At-Risk': 'alert' }
export function HealthBadge({ health }: { health?: PropertyHealth }) {
  if (!health) return <span className="dim">—</span>
  return <Badge tone={HEALTH_TONE[health]} variant="soft" dot>{health}</Badge>
}

const STANCE_TONE: Record<Stance, BadgeTone> = {
  Champion: 'ok', Ally: 'ok', Neutral: 'neutral', Skeptic: 'warn', Opponent: 'alert',
}
export function StanceBadge({ stance }: { stance?: Stance }) {
  if (!stance) return null
  return <Badge tone={STANCE_TONE[stance]} variant="soft">{stance}</Badge>
}

/* ---------- Chip ---------- */
export function Chip({ children, active, onClick }: { children: ReactNode; active?: boolean; onClick?: () => void }) {
  return <button className={`chip${active ? ' active' : ''}`} onClick={onClick}>{children}</button>
}

/* ---------- Avatar ---------- */
export function Avatar({ name, size = 32, tone = 'navy' }: { name: string; size?: number; tone?: 'navy' | 'blue' | 'cyan' | 'slate' }) {
  return (
    <span
      className={`avatar avatar--${tone}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
    >
      {initials(name)}
    </span>
  )
}

/* ---------- Empty state ---------- */
export function EmptyState({ icon, title, hint, action }: { icon?: ReactNode; title: string; hint?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-state-icon">{icon}</div>}
      <div className="empty-state-title">{title}</div>
      {hint && <div className="empty-state-hint">{hint}</div>}
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  )
}

/* ---------- Button ---------- */
export function Button({
  children, variant = 'default', size = 'md', onClick, icon, type = 'button', disabled,
}: {
  children?: ReactNode; variant?: 'primary' | 'default' | 'ghost' | 'navy'
  size?: 'sm' | 'md'; onClick?: () => void; icon?: ReactNode; type?: 'button' | 'submit'; disabled?: boolean
}) {
  return (
    <button className={`btn btn--${variant} btn--${size}`} onClick={onClick} type={type} disabled={disabled}>
      {icon}{children}
    </button>
  )
}
